import { useCallback, useEffect, useState } from "react";
import { fmtError, fmtEUR, changeOwnPassword } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Plus, Save, Clock, Users, KeyRound, Copy, Check, Trash2, RotateCcw, X } from "lucide-react";

const EMPTY = { name: "", description: "", duration_minutes: 60, price: 50, points_reward: 20, active: true };

export default function StaffSettingsPage() {
  const { api } = useTenant();
  const { user } = useAuth();
  // catalogo e prezzi: solo la titolare (il server rifiuta le modifiche dello staff)
  const isOwner = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!api) return;
    // anche i trattamenti spenti: restano visibili e si possono riattivare
    api.listAllServices().then(setItems).catch((e) => setError(fmtError(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (svc) => {
    setEditing(svc ? svc.id : "new");
    setForm(svc ? { ...svc } : EMPTY);
    setError("");
  };

  const handle = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
            : ["duration_minutes", "price", "points_reward"].includes(k) ? Number(e.target.value)
            : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (editing === "new") await api.createService(payload);
      else await api.updateService(editing, payload);
      await load();
      setEditing(null);
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!items) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;

  return (
    <div data-testid="settings-page" className="max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Impostazioni</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
            Catalogo <span className="italic">trattamenti.</span>
          </h1>
        </div>
        {isOwner && (
          <button
            data-testid="add-service"
            onClick={() => startEdit(null)}
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333]"
          >
            <Plus size={14} /> Nuovo trattamento
          </button>
        )}
      </div>
      {!isOwner && (
        <p className="-mt-4 mb-6 text-xs text-[#737373]">Solo la titolare può modificare catalogo e prezzi.</p>
      )}

      {error && <div className="mb-6 text-sm text-[#B91C1C]">{error}</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} data-testid={`service-${s.id}`}
            className={`rounded-3xl border border-black/5 bg-white p-6 tilt-card ${s.active === false ? "opacity-60" : ""}`}>
            {s.active === false && (
              <span data-testid={`service-inactive-${s.id}`}
                className="inline-block mb-2 text-[10px] tracking-[0.2em] uppercase border rounded-full px-2 py-0.5 border-black/10 bg-[#F5F5F5] text-[#737373]">
                Non attivo · non prenotabile online
              </span>
            )}
            <div className="font-serif text-2xl text-[#1A1A1A]">{s.name}</div>
            <div className="mt-1 text-sm text-[#4A4A4A] font-light line-clamp-2">{s.description || "—"}</div>
            <div className="mt-5 flex items-center justify-between text-sm text-[#4A4A4A]">
              <span>{s.duration_minutes} min</span>
              <span className="font-medium text-[#1A1A1A]">{fmtEUR(s.price)}</span>
              <span className="text-xs text-[#A78BFA]">+{s.points_reward} pt</span>
            </div>
            {isOwner && (
              <button
                data-testid={`edit-${s.id}`}
                onClick={() => startEdit(s)}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-white border border-black/10 text-[#1A1A1A] rounded-full px-4 py-2 text-xs hover:bg-[#FAFAFA]"
              >
                {s.active === false ? "Modifica / riattiva" : "Modifica"}
              </button>
            )}
          </div>
        ))}
      </div>

      <HoursSection />
      <TeamSection />
      <PasswordSection />

      {editing !== null && (
        <div data-testid="service-edit-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-3xl max-w-lg w-full p-8 border border-black/5">
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-5">
              {editing === "new" ? "Nuovo trattamento" : "Modifica trattamento"}
            </h2>
            <div className="space-y-4">
              <Field label="Nome">
                <input data-testid="svc-name" required value={form.name} onChange={handle("name")} className={inputCls} />
              </Field>
              <Field label="Descrizione">
                <textarea data-testid="svc-desc" rows={2} value={form.description || ""} onChange={handle("description")} className={`${inputCls} resize-none`} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Durata min">
                  <input data-testid="svc-duration" type="number" min="5" required value={form.duration_minutes} onChange={handle("duration_minutes")} className={inputCls} />
                </Field>
                <Field label="Prezzo €">
                  <input data-testid="svc-price" type="number" min="0" step="0.5" required value={form.price} onChange={handle("price")} className={inputCls} />
                </Field>
                <Field label="Punti">
                  <input data-testid="svc-points" type="number" min="0" required value={form.points_reward} onChange={handle("points_reward")} className={inputCls} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#4A4A4A] font-light">
                <input data-testid="svc-active" type="checkbox" checked={!!form.active} onChange={handle("active")} className="h-4 w-4 accent-[#1A1A1A]" />
                Attivo (visibile alle clienti)
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setEditing(null)} className="flex-1 bg-white border border-black/10 text-[#1A1A1A] rounded-full px-5 py-3 text-sm hover:bg-[#FAFAFA]">
                Annulla
              </button>
              <button
                type="submit"
                data-testid="svc-save"
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-3 text-sm hover:bg-[#333] disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA]";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">{label}</span>
    {children}
  </label>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="mt-16 mb-6 font-serif font-light text-3xl tracking-tight text-[#1A1A1A] flex items-center gap-3">
    <Icon size={22} className="text-[#A78BFA]" /> {children}
  </h2>
);

const DAYS = [
  ["mon", "Lunedì"], ["tue", "Martedì"], ["wed", "Mercoledì"], ["thu", "Giovedì"],
  ["fri", "Venerdì"], ["sat", "Sabato"], ["sun", "Domenica"],
];


// ---------- Fasce orarie per giorno (più fasce = pausa pranzo) ----------

const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

// Errore leggibile se le fasce di un giorno non sono valide (null se ok).
export function hoursError(hours) {
  for (const [key, label] of DAYS) {
    const ranges = [...(hours?.[key] || [])].sort((a, b) => toMin(a[0]) - toMin(b[0]));
    for (let i = 0; i < ranges.length; i++) {
      const [a, b] = ranges[i];
      if (!a || !b || toMin(a) >= toMin(b)) return `${label}: l'orario di fine deve essere dopo l'inizio (${a || "?"} → ${b || "?"})`;
      if (i > 0 && toMin(a) < toMin(ranges[i - 1][1])) return `${label}: le fasce ${ranges[i - 1][0]}-${ranges[i - 1][1]} e ${a}-${b} si sovrappongono`;
    }
  }
  return null;
}

// Nuova fascia: se il giorno è una fascia unica che copre l'ora di pranzo la
// divide (pausa 13-14), altrimenti ne aggiunge una dopo l'ultima.
function addRange(ranges) {
  const list = [...ranges];
  if (list.length === 1 && toMin(list[0][0]) < 13 * 60 && toMin(list[0][1]) > 14 * 60) {
    return [[list[0][0], "13:00"], ["14:00", list[0][1]]];
  }
  const lastEnd = list.length ? toMin(list[list.length - 1][1]) : 9 * 60 - 30;
  const start = Math.min(lastEnd + 30, 22 * 60);
  return [...list, [toHHMM(start), toHHMM(Math.min(start + 120, 23 * 60 + 59))]];
}

export function DayRangesEditor({ value, onChange, disabled, testPrefix = "hours", defaultRange = ["09:00", "19:00"] }) {
  const setDay = (d, ranges) => onChange({ ...value, [d]: ranges });
  return (
    <div className="space-y-3">
      {DAYS.map(([key, label]) => {
        const ranges = value?.[key] || [];
        return (
          <div key={key} className="flex items-start gap-3 flex-wrap" data-testid={`${testPrefix}-${key}`}>
            <label className="flex items-center gap-2 w-28 pt-2 text-sm text-[#1A1A1A]">
              <input
                type="checkbox"
                data-testid={`${testPrefix}-open-${key}`}
                disabled={disabled}
                checked={ranges.length > 0}
                onChange={(e) => setDay(key, e.target.checked ? [defaultRange] : [])}
                className="h-4 w-4 accent-[#1A1A1A]"
              />
              {label}
            </label>
            {ranges.length ? (
              <div className="flex flex-col gap-2">
                {ranges.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm flex-wrap">
                    <input type="time" data-testid={`${testPrefix}-from-${key}-${i}`} disabled={disabled} value={r[0]}
                      onChange={(e) => setDay(key, ranges.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))}
                      className="bg-[#FAFAFA] border border-black/10 rounded-xl px-3 py-2" />
                    <span className="text-[#737373]">→</span>
                    <input type="time" data-testid={`${testPrefix}-to-${key}-${i}`} disabled={disabled} value={r[1]}
                      onChange={(e) => setDay(key, ranges.map((x, j) => (j === i ? [x[0], e.target.value] : x)))}
                      className="bg-[#FAFAFA] border border-black/10 rounded-xl px-3 py-2" />
                    {!disabled && ranges.length > 1 && (
                      <button type="button" data-testid={`${testPrefix}-remove-${key}-${i}`} title="Rimuovi fascia"
                        onClick={() => setDay(key, ranges.filter((_, j) => j !== i))}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-black/10 text-[#737373] hover:bg-[#FAFAFA]">
                        <X size={12} />
                      </button>
                    )}
                    {!disabled && i === ranges.length - 1 && (
                      <button type="button" data-testid={`${testPrefix}-add-${key}`}
                        onClick={() => setDay(key, addRange(ranges))}
                        className="text-xs text-[#1A1A1A] underline underline-offset-4 hover:text-[#737373]">
                        + fascia (es. pausa pranzo)
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-sm text-[#737373] pt-2">Chiuso</span>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ---------- Orari di apertura ----------

function HoursSection() {
  const { api } = useTenant();
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!api) return;
    api.getSettings().then(setSettings).catch((e) => setError(fmtError(e)));
  }, [api]);

  if (!settings) return null;

  // Col gestionale collegato gli appuntamenti in parallelo li decidono le
  // operatrici libere: qui si imposta solo un tetto facoltativo (es. le cabine).
  const fromOperators = settings.capacity_source === "operators";

  const change = (patch) => { setMsg(""); setError(""); setSettings((s) => ({ ...s, ...patch })); };

  const save = async () => {
    const bad = hoursError(settings.business_hours);
    if (bad) { setError(bad); return; }
    setSaving(true);
    setError("");
    setMsg("");
    try {
      const payload = {
        business_hours: settings.business_hours,
        cancel_notice_hours: Math.max(0, Number(settings.cancel_notice_hours) || 0),
      };
      if (fromOperators) {
        const cap = Number(settings.max_parallel);
        payload.max_parallel = settings.max_parallel === "" || settings.max_parallel == null || !cap ? null : cap;
      } else {
        payload.concurrent_slots = Number(settings.concurrent_slots) || 1;
      }
      const updated = await api.updateSettings(payload);
      setSettings(updated);
      setMsg("Orari salvati: le clienti vedono già la nuova disponibilità.");
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="hours-section">
      <SectionTitle icon={Clock}>Orari di apertura</SectionTitle>
      <div className="rounded-3xl border border-black/5 bg-white p-6 md:p-8 max-w-2xl">
        <p className="text-sm text-[#4A4A4A] font-light mb-6">
          Le clienti possono prenotare online solo negli orari qui sotto. Per la pausa pranzo usa
          "+ fascia": per esempio 09:00 → 13:00 e 14:00 → 19:00. Gli appuntamenti già presi bloccano automaticamente gli slot.
        </p>
        <DayRangesEditor value={settings.business_hours} disabled={!isOwner}
          onChange={(bh) => change({ business_hours: bh })} />
        <div className="mt-6 flex items-start gap-4 flex-wrap">
          {fromOperators ? (
            <div className="max-w-md">
              <Field label="Tetto appuntamenti in parallelo (facoltativo)">
                <input
                  type="number" min="1" max="20" data-testid="max-parallel" disabled={!isOwner}
                  placeholder="nessun tetto"
                  value={settings.max_parallel ?? ""}
                  onChange={(e) => change({ max_parallel: e.target.value })}
                  className={`${inputCls} w-36`}
                />
              </Field>
              <p className="mt-2 text-xs text-[#737373]" data-testid="capacity-from-operators">
                Il gestionale è collegato: online si prenotano al massimo tante clienti quante sono le
                operatrici libere in quell'orario ({settings.operators_count} prenotabili online).
                Qui puoi mettere un tetto più basso, per esempio il numero di cabine. Vuoto = nessun tetto.
              </p>
            </div>
          ) : (
            <Field label="Appuntamenti in parallelo (cabine/operatrici)">
              <input
                type="number" min="1" max="20" data-testid="concurrent-slots" disabled={!isOwner}
                value={settings.concurrent_slots}
                onChange={(e) => change({ concurrent_slots: e.target.value })}
                className={`${inputCls} w-28`}
              />
            </Field>
          )}
          <div className="max-w-xs">
            <Field label="Disdetta online fino a (ore prima)">
              <input
                type="number" min="0" max="168" data-testid="cancel-notice" disabled={!isOwner}
                value={settings.cancel_notice_hours ?? 0}
                onChange={(e) => change({ cancel_notice_hours: e.target.value })}
                className={`${inputCls} w-28`}
              />
            </Field>
            <p className="mt-2 text-xs text-[#737373]">0 = la cliente può disdire dall'app fino all'orario dell'appuntamento.</p>
          </div>
        </div>
        {error && <div className="mt-4 text-sm text-[#B91C1C]" data-testid="hours-error">{error}</div>}
        {msg && <div className="mt-4 text-sm text-[#15803D]" data-testid="hours-saved">{msg}</div>}
        {isOwner ? (
          <button onClick={save} disabled={saving} data-testid="hours-save"
            className="mt-6 inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333] disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva orari
          </button>
        ) : (
          <div className="mt-6 text-xs text-[#737373]">Solo la titolare può modificare gli orari.</div>
        )}
      </div>
    </section>
  );
}


// ---------- Orari di lavoro di una persona (prenotazioni online) ----------

const ALL_DAYS_HOURS = { mon: [["09:00", "19:00"]], tue: [["09:00", "19:00"]], wed: [["09:00", "19:00"]],
  thu: [["09:00", "19:00"]], fri: [["09:00", "19:00"]], sat: [["09:00", "19:00"]], sun: [] };

function PersonHoursEditor({ name, hours, readOnly, onSave, onClose, testId }) {
  const [followCenter, setFollowCenter] = useState(!hours);
  const [value, setValue] = useState(hours || ALL_DAYS_HOURS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!followCenter) {
      const bad = hoursError(value);
      if (bad) { setError(bad); return; }
    }
    setBusy(true);
    setError("");
    try {
      await onSave(followCenter ? null : value);
      onClose();
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 w-full rounded-2xl border border-black/10 bg-[#FAFAFA] p-4" data-testid={testId}>
      <div className="text-sm text-[#1A1A1A] mb-3">Quando {name} è prenotabile online</div>
      {readOnly ? (
        <p className="text-xs text-[#737373] mb-3">Questi orari arrivano dal gestionale: modificali lì.</p>
      ) : (
        <label className="flex items-center gap-2 text-sm text-[#4A4A4A] mb-3">
          <input type="checkbox" checked={followCenter} onChange={(e) => setFollowCenter(e.target.checked)}
            data-testid={`${testId}-follow`} className="h-4 w-4 accent-[#1A1A1A]" />
          Come l'orario del centro
        </label>
      )}
      {!followCenter && (
        <DayRangesEditor value={value} onChange={setValue} disabled={readOnly || busy} testPrefix={`${testId}-h`} />
      )}
      {error && <div className="mt-3 text-sm text-[#B91C1C]">{error}</div>}
      <div className="mt-4 flex gap-2">
        {!readOnly && (
          <button type="button" onClick={save} disabled={busy} data-testid={`${testId}-save`}
            className="inline-flex items-center gap-1.5 text-xs bg-[#1A1A1A] text-white rounded-full px-4 py-2 hover:bg-[#333] disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salva orari
          </button>
        )}
        <button type="button" onClick={onClose} className="text-xs text-[#737373] hover:text-[#1A1A1A] px-2">Chiudi</button>
      </div>
    </div>
  );
}


// ---------- Team ----------

function TeamSection() {
  const { api } = useTenant();
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [staff, setStaff] = useState(null);
  const [operators, setOperators] = useState([]); // operatrici sincronizzate dal gestionale
  const [hoursOf, setHoursOf] = useState(null);   // id di chi sta modificando gli orari
  const [form, setForm] = useState(null); // {name, email, phone} | null
  const [creds, setCreds] = useState(null); // credenziali appena generate
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!api || !isOwner) return;
    api.listStaff().then(setStaff).catch((e) => setError(fmtError(e)));
    api.listOperators().then(setOperators).catch(() => setOperators([]));
  }, [api, isOwner]);
  useEffect(() => { load(); }, [load]);
  // col gestionale collegato si prenotano le sue operatrici, non gli account del sito
  const connected = operators.length > 0;

  if (!isOwner) return null;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.createStaff(form);
      setCreds(res.credentials);
      setForm(null);
      load();
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const resetPwd = async (u) => {
    if (!window.confirm(`Generare una nuova password per ${u.name}? Quella attuale smetterà di funzionare.`)) return;
    try {
      const res = await api.resetStaffPassword(u.id);
      setCreds(res.credentials);
    } catch (err) { setError(fmtError(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'account di ${u.name}? Non potrà più accedere.`)) return;
    try {
      await api.deleteStaff(u.id);
      load();
    } catch (err) { setError(fmtError(err)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.updateStaff(u.id, { active: u.active === false });
      load();
    } catch (err) { setError(fmtError(err)); }
  };

  return (
    <section data-testid="team-section">
      <SectionTitle icon={Users}>Il tuo team</SectionTitle>
      <div className="rounded-3xl border border-black/5 bg-white p-6 md:p-8 max-w-2xl">
        <p className="text-sm text-[#4A4A4A] font-light mb-6">
          Ogni dipendente ha il proprio accesso all'area staff: agenda, clienti e fidelity. Solo tu puoi gestire orari, catalogo e team.
        </p>
        {error && <div className="mb-4 text-sm text-[#B91C1C]">{error}</div>}
        <ul className="divide-y divide-black/5">
          {(staff || []).map((u) => (
            <li key={u.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`staff-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1A1A1A]">{u.name} {u.role === "admin" && <span className="text-[10px] tracking-[0.2em] uppercase text-[#A78BFA] ml-1">Titolare</span>}</div>
                <div className="text-xs text-[#737373] truncate">{u.email}</div>
              </div>
              {/* Prenotabile online: le clienti vedono le operatrici disattivate come "non disponibili" */}
              <button
                onClick={() => toggleActive(u)}
                data-testid={`staff-active-${u.id}`}
                title={u.active === false ? "Le clienti la vedono come non disponibile: clicca per riattivarla" : "Prenotabile online: clicca per renderla non disponibile"}
                className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-all ${
                  u.active === false
                    ? "border-black/10 bg-[#F5F5F5] text-[#737373]"
                    : "border-[#86EFAC] bg-[#F0FDF4] text-[#15803D]"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${u.active === false ? "bg-[#A3A3A3]" : "bg-[#22C55E]"}`} />
                {u.active === false ? "Non disponibile" : "Prenotabile"}
              </button>
              {!connected && (
                <button onClick={() => setHoursOf(hoursOf === u.id ? null : u.id)} data-testid={`staff-hours-${u.id}`}
                  title="Orari in cui è prenotabile online"
                  className="inline-flex items-center gap-1.5 text-xs border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]">
                  <Clock size={12} /> {u.hours ? "Orari personali" : "Orari"}
                </button>
              )}
              {u.role === "staff" && (
                <div className="flex gap-2">
                  <button onClick={() => resetPwd(u)} data-testid={`staff-reset-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]">
                    <RotateCcw size={12} /> Nuova password
                  </button>
                  <button onClick={() => remove(u)} data-testid={`staff-delete-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs border border-[#FCA5A5] text-[#B91C1C] rounded-full px-3 py-1.5 hover:bg-[#FEE2E2]">
                    <Trash2 size={12} /> Elimina
                  </button>
                </div>
              )}
              {hoursOf === u.id && (
                <PersonHoursEditor name={u.name} hours={u.hours} testId={`staff-hours-editor-${u.id}`}
                  onSave={(hours) => api.updateStaff(u.id, { hours }).then(load)}
                  onClose={() => setHoursOf(null)} />
              )}
            </li>
          ))}
        </ul>

        {connected && (
          <div className="mt-8" data-testid="operators-hours">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Operatrici dal gestionale</div>
            <p className="text-xs text-[#737373] mb-3">
              Online si prenotano le operatrici del gestionale. Se una lavora solo in certe ore (es. solo la mattina)
              indicalo qui: il sito la propone solo in quegli orari.
            </p>
            <ul className="divide-y divide-black/5">
              {operators.map((o) => (
                <li key={o.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`operator-${o.id}`}>
                  <div className="flex-1 min-w-0 text-sm text-[#1A1A1A]">
                    {o.name}
                    {!o.bookable_online && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#737373]">non prenotabile online</span>}
                    <div className="text-xs text-[#737373]">
                      {o.hours ? (o.hours_from_desktop ? "Orari dal gestionale" : "Orari personali") : "Come l'orario del centro"}
                    </div>
                  </div>
                  <button onClick={() => setHoursOf(hoursOf === o.id ? null : o.id)} data-testid={`operator-hours-${o.id}`}
                    className="inline-flex items-center gap-1.5 text-xs border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]">
                    <Clock size={12} /> Orari
                  </button>
                  {hoursOf === o.id && (
                    <PersonHoursEditor name={o.name} hours={o.hours} readOnly={o.hours_from_desktop}
                      testId={`operator-hours-editor-${o.id}`}
                      onSave={(hours) => api.updateOperator(o.id, { hours }).then(load)}
                      onClose={() => setHoursOf(null)} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {creds && <CredentialsBox creds={creds} onClose={() => setCreds(null)} />}

        {form ? (
          <form onSubmit={create} className="mt-6 space-y-4" data-testid="staff-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nome e cognome">
                <input data-testid="staff-name" required minLength={2} value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
              </Field>
              <Field label="Email">
                <input data-testid="staff-email" type="email" required value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
              </Field>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setForm(null)}
                className="flex-1 bg-white border border-black/10 rounded-full px-5 py-2.5 text-sm hover:bg-[#FAFAFA]">Annulla</button>
              <button type="submit" disabled={busy} data-testid="staff-create"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333] disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crea accesso
              </button>
            </div>
          </form>
        ) : (
          <button onClick={() => { setForm({ name: "", email: "" }); setCreds(null); }} data-testid="staff-add"
            className="mt-6 inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333]">
            <Plus size={14} /> Aggiungi dipendente
          </button>
        )}
      </div>
    </section>
  );
}

function CredentialsBox({ creds, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { console.error("clipboard copy failed", e); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-[#A78BFA]/40 bg-[#F5F3FF] p-5" data-testid="staff-credentials">
      <div className="text-sm text-[#1A1A1A] mb-2">
        Credenziali generate — <strong>consegnale ora alla dipendente</strong>: la password non sarà più visibile.
      </div>
      <div className="text-sm"><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Email</span> <code>{creds.email}</code></div>
      <div className="text-sm mt-1"><span className="text-[#737373] text-xs uppercase tracking-[0.2em] mr-2">Password</span> <code data-testid="staff-generated-password">{creds.password}</code></div>
      <div className="mt-3 flex gap-2">
        <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs bg-white border border-black/10 rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]">
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copiato" : "Copia credenziali"}
        </button>
        <button onClick={onClose} className="text-xs text-[#737373] hover:text-[#1A1A1A] px-2">Chiudi</button>
      </div>
    </div>
  );
}


// ---------- Cambia password ----------

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMsg("");
    if (next !== confirm) { setError("Le due password nuove non coincidono"); return; }
    if (next.length < 8) { setError("La nuova password deve avere almeno 8 caratteri"); return; }
    setBusy(true);
    try {
      await changeOwnPassword(current, next);
      setMsg("Password aggiornata. Usa la nuova dal prossimo accesso.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="password-section">
      <SectionTitle icon={KeyRound}>La tua password</SectionTitle>
      <form onSubmit={submit} className="rounded-3xl border border-black/5 bg-white p-6 md:p-8 max-w-2xl space-y-4">
        <Field label="Password attuale">
          <input data-testid="pwd-current" type="password" required autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nuova password (min 8 caratteri)">
            <input data-testid="pwd-new" type="password" required autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Ripeti nuova password">
            <input data-testid="pwd-confirm" type="password" required autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
          </Field>
        </div>
        {error && <div className="text-sm text-[#B91C1C]" data-testid="pwd-error">{error}</div>}
        {msg && <div className="text-sm text-[#15803D]" data-testid="pwd-success">{msg}</div>}
        <button type="submit" disabled={busy} data-testid="pwd-save"
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#333] disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Cambia password
        </button>
      </form>
    </section>
  );
}
